<template>
  <div v-if="!hasNoTemplate" id="mediaMenu" ref="mediaMenu" :class="['right-side-menu', accessorComponent, showFluidLayout ? 'fluid-layout' : '']">
    <!-- Form Tab Icons -->
    <div
      v-if="(!showFluidLayout && formTabStyle) || (showFluidLayout && showCommonPropertiesTabIcon)"
      id="mediaTabIcons"
    >
      <el-tooltip
        :popper-class="'custom-tooltip-popper capitalize-text'"
        :show-arrow="false"
        effect="light"
        :content="$t('label.edit')"
        placement="bottom"
        :index="'mediaTabIconTooltipEdit'"
        class="item">
        <el-button @click="formTabSelected = formTabOptions.commonProperties"
          :class="['cstm-icon-btn-medium', 'pencil_circle_grey', {'visible': showCommonPropertiesTabIcon,'is-selected': formTabSelected === formTabOptions.commonProperties}]">
        </el-button>
      </el-tooltip>

      <el-tooltip
        :popper-class="'custom-tooltip-popper capitalize-text'"
        :show-arrow="false"
        effect="light"
        :content="$t('label.preview')"
        placement="bottom"
        :index="'mediaTabIconTooltipPreview'"
        class="item">
        <el-button @click="formTabSelected = formTabOptions.scheduleProperty"
          :class="['cstm-icon-btn-medium', 'play_circle_grey', {'visible': showSchedulePropertyTabIcon, 'is-selected': formTabSelected === null || formTabSelected === formTabOptions.scheduleProperty}]">
        </el-button>
      </el-tooltip>
    </div>

    <el-form
      ref="mediaPropertiesForm"
      id="mediaPropertiesForm"
      :model="mediaPropertiesForm.values"
      :class="['mediaPropertiesForm hide-scrollbar']"
      @submit.prevent
    >
      <template v-for="(property, propertyKey) in mediaProperties">
        <el-form-item v-if="hiddenMenuItems.indexOf(property.id) < 0" :key="propertyKey" :class="['property', property.id]" :id="property.id">

          <!-- Common Properties Tab -->
          <template v-if="formTabSelected === formTabOptions.commonProperties">
            <!-- Duration -->
            <div v-if="(property.id === mediaPropertiesId.duration)" class="section-wrapper">
              <div class="title">{{$t('label.duration')}}</div>
              <div class="content duration">
                <template v-if="enableEditMediaDuration">
                  <duration-input ref="mediaDurationInput"
                    :model-value="mediaPropertiesForm.values.duration"
                    @blur="editableMediaProperties.splice(editableMediaProperties.indexOf('duration'), 1)"
                    @update:modelValue="handleDurationchanged"
                    :default-value="defaultMediaDurationFormatted"
                    @click="handlePropertyOnClick(property.id)"
                  />
                </template>
                <template v-else>
                  <duration-input v-if="mediaPropertiesForm.values.duration !== 'clickToEdit'"
                    :model-value="mediaPropertiesForm.values.duration"
                    :default-value="defaultMediaDurationFormatted"
                    readonly="true"
                    disabled="true" />
                </template>
              </div>
            </div>
          </template>

          <!-- Schedule Property Tab -->
          <template v-if="[formTabOptions.scheduleProperty, null].indexOf(formTabSelected) >= 0">
            <!-- Tags -->
            <div v-if="(property.id === mediaPropertiesId.tags) && isTagMenuItemVisible" class="section-wrapper">
              <div class="title">{{property.name}}</div>

              <div class="content tags">
                <tag-menu-item v-if="mediaSelection.length > 0"
                  :tagsSummaryProp="tagsSummary"
                  :selectedTagsFromSliderProp="selectedTagsFromSlider"
                  :tagsProp="mediaPropertiesForm.values.tags"
                  :accessorComponentProp="accessorComponent"
                  @tags-change="handleSubComponentChange($event, 'tagMenuItem')">
                </tag-menu-item>
              </div>
            </div>

            <!-- PoP Identifier -->
            <div v-if="property.id === mediaPropertiesId.popIdentifier && showPopId" class="section-wrapper">
              <div class="title">{{property.name}}</div>

              <div class="content popIdentifier">
                <pop-identifier-item v-if="mediaSelection.length > 0"
                  :popIdsProp="popIdsSummary"
                  :popValueProp="mediaPropertiesForm.values.popId"
                  :accessorComponentProp="accessorComponent"
                  @pop-identifier-change="handleSubComponentChange($event, 'popIdentifierMenuItem')">
                </pop-identifier-item>
              </div>
            </div>

            <!-- Schedules -->
            <div v-if="property.id === mediaPropertiesId.schedules" :class="['w-full m-0 content schedule', { 'schedule-alert': isMediaAlertType}]">
              <el-form-item prop="schedule" class="schedule form-item">
                <!-- Presentation -->
                <div v-if="!property.editable || editableMediaProperties.indexOf('schedules') >= 0" :class="['presentation', {'change-sched-target-disabled': !enableScheduleChangeTarget}]">
                  <el-checkbox-group
                    class="m-[1px]"
                    :model-value="publishOption"
                    @change="handlePublishOption"
                    @update:model-value="(newValue) => publishOption = newValue"
                  >
                    <div class="section-wrapper publish__option">
                      <el-checkbox v-if="(accessorComponent !== 'schedulePage')" class="title" value="player" :disabled="!enableScheduleChangeTarget">{{ isMediaAlertType ? $t('label.sendTo') : $t('label.publishTo') }}</el-checkbox>

                      <!-- Schedule List -->
                      <template v-if="mediaPropertiesForm.values.schedules.length > 0 && accessorComponent !== 'schedulePage'">
                        <!-- User defined schedule target -->
                        <div class="schedule-target-list user-defined">
                          <template v-for="(schedule, indx) in mediaPropertiesForm.values.schedules">
                            <div v-if="schedule.userDefined" :key="indx" :class="{'schedule-target': true, 'is-selected': schedule._edit.isSelected, 'not-visible': schedule._edit.notVisible}">
                              <div class="schedule-target-background" :style="{backgroundColor: getPlayerTagsColor(schedule.name, schedule._edit.backgroundColor)}"></div>
                              <div @click="handleScheduleListItemClick($event, schedule)"
                                :class="['schedule-target-name', {'selected-player-name': schedule._edit.isSelected}]">{{schedule.name}}</div>
                              <div v-if="enableScheduleChangeTarget" @click="handleScheduleListItemDeleteClick(schedule)" class="remove-schedule-target"></div>
                            </div>
                          </template>
                        </div>

                        <!-- Not User defined schedule target (does not include smart sync tag) -->
                        <div class="schedule-target-list not-user-defined">
                          <template v-for="(schedule, indx) in mediaPropertiesForm.values.schedules">
                            <div v-if="!schedule.userDefined && schedule.target !== 'smart-tags'" :key="indx" :class="{'schedule-target': true, 'is-selected': schedule._edit.isSelected, 'not-visible': schedule._edit.notVisible}">
                              <div class="schedule-target-background" :style="{backgroundColor: schedule._edit.backgroundColor }"></div>
                              <div @click="handleScheduleListItemClick($event, schedule)" :class="['schedule-target-name', schedule.target, {'selected-player-name': schedule._edit.isSelected}]">
                                {{ schedule.name === 'ALL' ? 'All Players' : schedule.name }}
                              </div>
                              <div v-if="enableScheduleChangeTarget" @click="handleScheduleListItemDeleteClick(schedule)" class="remove-schedule-target"></div>
                            </div>
                          </template>
                        </div>
                      </template>

                      <!-- New Schedule Target and Play time form -->
                      <template v-if="enableScheduleChangeTarget">
                        <PublishSelectionMenuItem
                          v-if="mediaPropertiesForm.values.scheduleNewTargetDropdownVisible && accessorComponent !== 'schedulePage'"
                          :ledResolutionLabel="ledResolutionLabel"
                          :allSchedules="userSchedules"
                          :allAvailbleUnselectedSchedules="mediaPropertiesForm.values.scheduleNewTargetOptions"
                          :getPlayerTagsColor="getPlayerTagsColor"
                          @selectPlayerOption="handlePlayerOptionSelection"
                          @hideDropdown="mediaPropertiesForm.values.scheduleNewTargetDropdownVisible = false"
                        />

                        <!-- Show New Schedule target dropdown icon -->
                        <el-button v-if="mediaPropertiesForm.values.scheduleNewTargetIconVisible && !mediaPropertiesForm.values.scheduleNewTargetDropdownVisible"
                          @click="showScheduleDropdownClick"
                          :class="['show-new-schedule-target-dropdown', {'has-schedules': mediaPropertiesForm.values.schedules.length}]">
                        </el-button>
                      </template>
                    </div>

                    <!-- Smart Sync Option -->
                    <div class="section-wrapper publish__option is-smartsync" v-if="getSmartSyncTag">
                      <el-checkbox v-if="(accessorComponent !== 'schedulePage')" class="title" value="smartSync" :disabled="!enableScheduleChangeTarget">Smart Sync</el-checkbox>
                      <div class="question-icon" v-if="smartSyncHelpLink" @click="handleSmartSyncHelpClick"></div>
                    </div>
                  </el-checkbox-group>

                  <!-- Schedule Play Time Form -->
                  <div v-if="enableScheduleChangeTarget && mediaPropertiesForm.values.scheduleSelection.length > 0 && !mediaPropertiesForm.values.scheduleAlert"
                    :class="['section-wrapper', {'schedule-play-time': true, 'no-margin-top': (accessorComponent === 'schedulePage')}]">
                    <div class="title">{{$t('label.playTime')}}</div>

                    <div class="content">
                      <schedule-playtime-menu-item
                        ref="schedulePlaytime"
                        :parentAccessorComponentProp="accessorComponent"
                        :mediaSelectionProp="mediaSelection"
                        :mediaPropertiesFormDataProp="mediaPropertiesForm"
                        @schedule-playtime-menu-item-change="handleSubComponentChange($event, 'schedulePlaytimeMenuItem')">
                      </schedule-playtime-menu-item>
                    </div>
                  </div>
                </div>
              </el-form-item>
            </div>
          </template>

          <!-- Validity Property -->
          <div v-if="property.id === 'validity'" class="content validity">
            <el-form-item prop="validity" class="validity form-item section-wrapper">
              <div class="title">{{property.id}}</div>

              <!-- Presentation -->
              <div v-if="!property.editable" @click="handlePropertyOnClick(property.id)" class="presentation">
                <template v-if="mediaPropertiesForm.values.validityPresentation !== 'NONE'">
                  {{mediaPropertiesForm.values.validityPresentation}}
                </template>
                <template v-else>
                  <el-icon class="no-validity-icon el-icon-date"><Calendar /></el-icon>
                </template>
              </div>

              <!-- Editable -->
              <div v-if="property.editable" class="editable">
                <el-date-picker
                  :ref="`form-el-${property.id}`"
                  v-model="mediaPropertiesForm.values.validity"
                  value-format="yyyy-MM-dd"
                  format="dd MMM yyyy"
                  @change="handlePropertyChange(property.id)"
                  type="daterange"
                  :start-placeholder="$t('label.start')" :end-placeholder="$t('label.end')"
                  popper-class="validity-property-date-range"
                  class="cstm-range-datepicker-grey date-range">
                </el-date-picker>
              </div>
            </el-form-item>
          </div>

          <!-- Delete Property Button -->
          <el-button v-if="property.id === 'delete' && (editableMediaProperties.length < 1) && mediaPropertiesToHide.indexOf('delete') < 0"
            @click="handleMediaPropertyDeleteClick"
            class="cstm-btn-grey width-125">
            {{property.name}}
          </el-button>
        </el-form-item>
      </template>

      <slot name="addMenuItemSlot"></slot>

      <!-- Submit Button -->
      <template v-if="['mediaPage', 'ledPage'].indexOf(accessorComponent) >= 0">
        <!-- Alert media -->
        <template v-if="isMediaAlertType">
          <el-button
            v-if="getAlertActionButtonProp.isVisible"
            @click="getAlertActionButtonProp.onClick()"
            class="cstm-btn-gradient width-125 alert-submit">
            {{getAlertActionButtonProp.label}}
          </el-button>
        </template>

        <!-- Normal media files -->
        <template v-if="!isMediaAlertType">
          <el-form-item v-if="editableMediaProperties.length > 0 || editedMediaProperties.length" class="submit-btn-content">
          <el-button class="cstm-btn-gradient width-125"
            @click="handleMediaPropertiesFormSubmit">{{$t('label.ok')}}
          </el-button>
        </el-form-item>
        </template>
      </template>
      <template v-else-if="accessorComponent === 'schedulePage'">
        <el-form-item v-if="editedMediaProperties.length > 0" class="submit-btn-content">
          <el-button class="cstm-btn-gradient width-125"
            @click="handleMediaPropertiesFormSubmit">{{$t('label.ok')}}
          </el-button>
        </el-form-item>
      </template>
    </el-form>
  </div>
</template>
<script src="./media-menu.js"></script>
<style lang="scss" src="./media-menu.scss"></style>
